/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { message } from '@brokerbay/design-components';
import UserMlsRoster from '@bb/domain/dist/types/UserMlsRoster';
import { Button, Collapse, Text } from 'components';
import { omit, pick } from 'lodash';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import upsertOneRosterQa from '../../../../api/UserMlsRosterController/upsertOneRosterQa';
import RosterDataViewerPageContext from './RosterDataViewerPageContext';

const EXCLUDED_KEYS: Partial<Record<keyof UserMlsRoster, true>> = {
  // @ts-expect-error
  _id: true,
  id: true,
  createdAt: true,
  updatedAt: true,
};

// Use this type once babel is updated to handle satisfies keyword: as const satisfies Readonly<(keyof UserMlsRoster)[]>
const UNEDITABLE_FIELDS: (keyof UserMlsRoster)[] = ['mlsType', 'feed'];

const RosterEditor: React.FC = () => {
  const { roster, rosterType, updates, setUpdates } = useContext(
    RosterDataViewerPageContext,
  );

  const processedRoster = omit(roster, Object.keys(EXCLUDED_KEYS));

  /** Used to trigger a full re-render so that uncontrolled inputs update when a new roster is selected */
  const [rootKey, setRootKey] = useState(0);

  useEffect(() => {
    setRootKey(prev => prev + 1);
  }, [roster]);

  if (!roster) return null;

  const submit = async () => {
    try {
      const { newEntryCreated } = await upsertOneRosterQa({
        rosterType,
        updates: { ...updates!, ...pick(roster, UNEDITABLE_FIELDS) },
      });
      message.success(
        newEntryCreated
          ? 'A new roster entry was created successfully'
          : 'Updated Successfully',
      );
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to submit');
    }
  };

  return (
    <Fragment key={rootKey}>
      <Collapse>
        <Collapse.Panel header="Roster Data" key="1">
          <StyledTextarea
            rows={12}
            defaultValue={JSON.stringify(processedRoster, null, 2)}
            onBlur={e => {
              try {
                const object = JSON.parse(e.target.value);
                setUpdates(object);
              } catch (error) {
                message.error('Invalid JSON. Reverting value.');
                e.currentTarget.value = e.currentTarget.defaultValue;
                setUpdates(processedRoster as UserMlsRoster);
              }
            }}
          />
        </Collapse.Panel>
      </Collapse>

      <Text>
        Please note that removing fields and updating the following fields will
        not work:{' '}
        {UNEDITABLE_FIELDS.map(k => (
          <kbd key={k}>{k}</kbd>
        ))}
      </Text>

      <StyledButton type="primary" onClick={submit} disabled={!updates}>
        Submit
      </StyledButton>
    </Fragment>
  );
};

export default RosterEditor;

const StyledButton = styled(Button)`
  width: fit-content;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
`;
